import {
  Box,
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent,
  DialogContentClassKey,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/core/styles';
import { WithStyles } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { Close, Crop54, Crop75, InsertDriveFile } from '@material-ui/icons';
import { ClassNameMap } from '@material-ui/styles';
import React, { ReactNode } from 'react';

export enum DialogState {
  ADD,
  EDIT,
}

export interface StandardDialogProps extends DialogProps {
  dialogContentclasses?: Partial<ClassNameMap<DialogContentClassKey>>;
  title?: any;
  subTitle?: React.ReactNode | string;
  rephraseClass?: boolean;
  handleClose?: React.ReactEventHandler<{}>;
  handleExpand?: React.ReactEventHandler<{}>;
  dialogContent: ReactNode;
  dialogActions?: ReactNode;
  removeContentBox?: boolean;
  overflowDialog?: boolean;
  dialogContentRef?: React.RefObject<HTMLElement>;
  dialogLogo?: ReactNode;
}

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  subTitle?: React.ReactNode | string;
  children: React.ReactNode;
  onClose: React.MouseEventHandler<{}>
  logo?: React.ReactNode;
  onExpand?: React.MouseEventHandler<{}>;
}

const styles = (theme: Theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    closeButtonLogo: {
      position: 'absolute',
      right: '20px',
      color: theme.palette.grey[500],
    },
    expandButtonLogo: {
      position: 'absolute',
      right: '80px',
      color: theme.palette.grey[500],
    },
    logo: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      backgroundColor: "#EAEAEA",
      marginRight: "15px"
    }
  });

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  let subTitle = props.subTitle;
  if (subTitle && typeof subTitle === 'string') subTitle = (<Typography style={{ fontSize: 'smaller' }}>{subTitle}</Typography>);

  return (
    <MuiDialogTitle disableTypography {...other}>
      <Typography variant="h4">{children}</Typography>
      {subTitle}
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogTitleWithLogo = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, logo, onExpand, ...other } = props;
  let subTitle = props.subTitle;
  if (subTitle && typeof subTitle === 'string') subTitle = (<Typography style={{ fontSize: 'smaller' }}>{subTitle}</Typography>);

  return (
    <MuiDialogTitle disableTypography {...other}>
      <Box display="flex" alignItems="center">
        <Box className={classes.logo}>
          {logo}
        </Box>
        <Box>
          <Typography variant="h4">{children}</Typography>
          {subTitle}
        </Box>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButtonLogo}
            onClick={onClose}
            edge="end"
          >
            <Close />
          </IconButton>
        ) : null}
        {onExpand ? (
          <IconButton
            aria-label="expand"
            className={classes.expandButtonLogo}
            onClick={onExpand}
            edge="end"
          >
            <Crop54 />
          </IconButton>
        ) : null}
      </Box>
    </MuiDialogTitle>
  );
});

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2, 3),
  },
}))(MuiDialogActions);

const useStyles = makeStyles(() => ({
  dialogRootFullScreen: {
    height: 'calc(100vh - 64px)',
    top: '64px !important',
  },
  dialogPaperFullScreenRoudedBorders: {
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
  },
  dialogPaperFullScreen: {
    overflow: 'visible',
    margin: 'auto',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
  },
  dialogContent: {
    display: 'flex',
  },
  dialogContentWithOverflow: {
    display: 'flex',
    overflow: 'visible',
  },
  dialogPaper: {
    overflow: 'visible',
    margin: 'auto',
  },
  dialogContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const StandardDialog = ({
  title,
  handleClose,
  handleExpand,
  dialogActions,
  dialogContent,
  dialogContentclasses,
  dialogContentRef,
  dialogLogo,
  overflowDialog,
  removeContentBox,
  ...props
}: StandardDialogProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const { fullWidth, fullScreen, maxWidth, ...otherProps } = props;

  const defaultFullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const handleDialogClose = (e, reason) => {
    if (reason == 'backdropClick') return;
    handleClose && handleClose(e);
  };

  const _classes: any = {};
  if (fullScreen) {
    _classes.root = classes.dialogRootFullScreen;
    _classes.paper = classes.dialogPaperFullScreenRoudedBorders;
  }
  if (props.rephraseClass) {
    _classes.paper = fullScreen
      ? classes.dialogPaperFullScreen
      : classes.dialogPaper;
  }

  return (
    <MuiDialog
      classes={_classes}
      onClose={handleDialogClose}
      fullWidth={fullWidth ?? true}
      fullScreen={fullScreen ?? defaultFullScreen}
      maxWidth={maxWidth ?? 'md'}
      aria-labelledby="form-dialog-title"
      {...otherProps}
    >
      {dialogLogo ?
        <DialogTitleWithLogo
          id="form-dialog-title"
          onClose={handleClose ? () => handleClose(null) : null}
          onExpand={handleExpand ? () => handleExpand(null) : null}
          subTitle={props.subTitle}
          logo={dialogLogo}
        >
          {title}
        </DialogTitleWithLogo>
        :
        <DialogTitle
          id="form-dialog-title"
          onClose={handleClose ? () => handleClose(null) : null}
          subTitle={props.subTitle}
        >
          {title}
        </DialogTitle>}


      <DialogContent ref={dialogContentRef} className={overflowDialog ? classes.dialogContentWithOverflow : classes.dialogContent} classes={dialogContentclasses}>
        {removeContentBox ? (
          dialogContent
        ) : (
          <Box className={`dialog-content-box`} m={4} flexGrow={1}>
            {dialogContent}
          </Box>
        )}
      </DialogContent>

      {dialogActions && (
        <DialogActions disableSpacing>{dialogActions}</DialogActions>
      )}
    </MuiDialog>
  );
};
